/**
 * CampanaDetallePage — `/activa/campanas/:id` · CENTRO DE CONTROL de una campaña.
 *
 * Orden de lectura: 6 métricas → avisos → entrega → grupos → respuestas → líneas → el resto.
 *
 * Regla que manda sobre cualquier otra: si un dato no se puede medir, se dice "sin medir"
 * con el porqué — nunca un 0 disfrazado de medición ni un fallback a datos demo. Y si los
 * números salieron de un transporte simulado, la pantalla lo grita antes que nada.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, AlertTriangle, RefreshCw, Share2, Download, Send,
  TrendingUp, Smartphone, ChevronDown, ChevronRight, ChevronLeft, ShieldCheck,
  Users, Clock, FileText, ScrollText, CheckCheck, MessageSquare, Ban,
  BarChart3, Activity, Star, UploadCloud, BadgeCheck, Split,
} from "lucide-react";
import { activaApi } from "../api";
import { useEsEquipoDeenex } from "../hooks/useEsEquipoDeenex";
import { esEquipoDeenex } from "../rolActiva";
import DeliveryFunnel from "../components/DeliveryFunnel";
import ChipEstado, { estadoInfo } from "../components/ChipEstado";
import StatTile from "../components/StatTile";
import { ConfirmarEnvio } from "../components/ConfirmarEnvio";
import { FirmaComercio } from "../components/FirmaComercio";
import { marcaQueFirma } from "../utils/marcaCampana";
import { compact, num, fecha, fechaDia, fechaHora, mesLargo, pct, plural } from "../utils/format";
import { etiquetaEstadoLinea } from "../utils/estadoLinea";
import { descargarCSV, slug } from "../utils/csv";
import { mensaje422Mapeo } from "../utils/exigirOk";
import type { CampanaReal, ResultadosReal, RespuestaReal, LineaReal, MedicionReal, EstadoCampana, BitacoraReal, EventoReal } from "../types.real";
import { etiquetaOferta } from "../types.real";
import { motivoNoOficial, seguirPorOficial } from "../utils/porOficial";
import { comoEnviarDeVerdad } from "../utils/avisoSimulados";
import { porQueBajoElDescuento, textoBajaDescuento } from "../utils/grupos";
import { useAlcanceOficial } from "../../wa-oficial/hooks/useAlcanceOficial";
import { AlcanceOficial } from "../../wa-oficial/components/AlcanceOficial";
import { RepartoWhatsApp } from "../components/RepartoWhatsApp";
import { porQueDifieren } from "../../wa-oficial/logic/alcance";
import { usePoliticaConsentimiento } from "../hooks/usePoliticaConsentimiento";
import { textoDeCsv } from "../../wa-oficial/logic/archivo";

const yaEnvio = (e?: EstadoCampana) => e === "enviando" || e === "sent" || e === "measured";

const CHIP = {
  medido: { texto: "medido", cls: "bg-emerald-50 text-emerald-700" },
  sinMedir: { texto: "sin medir", cls: "bg-slate-100 text-slate-500" },
  simulado: { texto: "simulado", cls: "bg-amber-100 text-amber-700" },
};

/** Estado de entrega de un mensaje, con la semántica de WhatsApp. */
function EstadoEntrega({ e }: { e: EventoReal }) {
  // los eventos viejos (previos al tracking de recibos) no tienen estadoEntrega: lo inferimos
  const st = e.estadoEntrega || (e.rebote ? "fallido" : e.leido ? "leido" : e.entregado ? "entregado" : e.enviado ? "enviado" : "pendiente");
  const MAP: Record<string, { l: string; cls: string }> = {
    pendiente: { l: "pendiente", cls: "bg-slate-100 text-slate-500" },
    // "simulado" = el transporte era un mock: NO salió nada. Se dice con esas palabras.
    simulado: { l: "no salió", cls: "bg-amber-100 text-amber-700" },
    enviado: { l: "enviado", cls: "bg-slate-100 text-slate-600" },
    entregado: { l: "entregado ✓✓", cls: "bg-slate-100 text-slate-700" },
    leido: { l: "leído ✓✓", cls: "bg-sky-50 text-sky-600" },
    fallido: { l: "fallido", cls: "bg-red-50 text-red-600" },
  };
  const x = MAP[st] || MAP.pendiente;
  // El transporte manda sobre el estado: un evento puede decir "enviado" y haber salido por un
  // mock. `transporte` es el dato que no miente aunque alguien se olvide la env — sin esto el
  // agregado dice "hay N simulados" pero el operador no puede señalar CUÁLES.
  const noSalio = e.transporte === "simulated" || e.transporte === "smtp_mock";
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${noSalio ? "bg-amber-100 text-amber-700" : x.cls}`}>
        {noSalio ? "no salió" : x.l}
      </span>
      {noSalio && (
        <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200" title={e.transporte === "smtp_mock" ? "No salió: no hay una casilla de email conectada" : "No salió de verdad: modo de prueba"}>
          {e.transporte === "smtp_mock" ? "sin casilla" : "de prueba"}
        </span>
      )}
    </span>
  );
}

/** Encabezado de sección — la jerarquía la da el título, no una caja más. */
/* ── Pestañas del centro de control ──────────────────────────────────────── */

type TabKey = "stats" | "salud" | "ficha" | "mensajes";

const TABS: { key: TabKey; label: string; icon: any }[] = [
  { key: "stats", label: "Estadísticas", icon: BarChart3 },
  { key: "salud", label: "Salud del WhatsApp", icon: Activity },
  { key: "ficha", label: "Ficha de la campaña", icon: FileText },
  { key: "mensajes", label: "Mensajes", icon: MessageSquare },
];

/**
 * Switch de pestañas. Segmented control: se ve de un vistazo dónde estás y qué más hay.
 * `alerta` pinta un punto en la pestaña que tiene algo que mirar (líneas caídas, por ejemplo)
 * para que un problema no quede escondido detrás de una pestaña que nadie abre.
 */
function Tabs({ value, onChange, alerta, tabs = TABS }: { value: TabKey; onChange: (t: TabKey) => void; alerta?: Partial<Record<TabKey, boolean>>; tabs?: typeof TABS }) {
  /**
   * Navegación con flechas + roving tabindex: es parte del CONTRATO de `role="tablist"`.
   * Sin esto, un lector de pantalla anuncia "pestaña 1 de 4", el usuario aprieta → y no pasa
   * nada. Declarar el rol sin cumplirlo es PEOR que usar botones comunes: promete un
   * comportamiento que no existe. Con roving tabindex, Tab entra y sale del grupo (una parada,
   * no cuatro) y las flechas se mueven dentro.
   */
  const onKeyDown = (e: React.KeyboardEvent) => {
    const i = tabs.findIndex((t) => t.key === value);
    let next = -1;
    if (e.key === "ArrowRight") next = (i + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    onChange(tabs[next].key);
    // mover el foco con la selección: si no, el lector sigue leyendo la pestaña vieja
    document.getElementById(`tab-${tabs[next].key}`)?.focus();
  };

  return (
    // scroll horizontal en mobile: 4 pestañas no entran en 375px y romperían el layout
    <div
      role="tablist"
      aria-label="Secciones de la campaña"
      onKeyDown={onKeyDown}
      className="flex items-center gap-1 rounded-xl bg-slate-100 p-1 overflow-x-auto"
    >
      {tabs.map((t) => {
        const activo = value === t.key;
        const Icon = t.icon;
        return (
          <button
            key={t.key}
            id={`tab-${t.key}`}
            role="tab"
            aria-selected={activo}
            aria-controls={`panel-${t.key}`}
            tabIndex={activo ? 0 : -1}
            onClick={() => onChange(t.key)}
            className={`relative flex-shrink-0 h-9 px-3.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
              activo ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon size={14} className={activo ? "text-violet-600" : "text-slate-400"} />
            {t.label}
            {/* el punto de alerta era invisible para un lector de pantalla: sin texto no existe */}
            {alerta?.[t.key] && (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" aria-hidden="true" />
                <span className="sr-only">(hay algo para revisar)</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

function SecTitulo({ titulo, sub, right }: { titulo: string; sub?: string; right?: React.ReactNode }) {
  return (
    <header className="flex items-end justify-between gap-3 mb-5">
      <div>
        <h2 className="text-base font-semibold text-slate-900 tracking-tight">{titulo}</h2>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
      {right}
    </header>
  );
}

export default function CampanaDetallePage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  /**
   * Una persona de un cliente (Accesos, rol «comercio») ve la campaña, sus números y sus mensajes;
   * reanudar, medir, compartir el reporte y las líneas de WhatsApp son del equipo (el backend se los
   * niega con 403 SOLO_EQUIPO). Sin las líneas no hay pestaña «Salud del WhatsApp».
   */
  const equipo = useEsEquipoDeenex();
  // «Reanudar envío» es del equipo: a un cliente no se le dice que toque un botón que no tiene
  const reanudar = equipo ? "tocá «Reanudar envío»" : "lo reanuda el equipo de Deenex";
  const [campana, setCampana] = useState<CampanaReal | null>(null);
  const [results, setResults] = useState<ResultadosReal | null>(null);
  const [responses, setResponses] = useState<RespuestaReal[]>([]);
  const [lines, setLines] = useState<LineaReal[]>([]);
  const [medicion, setMedicion] = useState<MedicionReal | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  /** el fetch de resultados FALLÓ (≠ "la campaña no tiene resultados"): no pintar ceros */
  const [resultsError, setResultsError] = useState("");
  /** no pudimos consultar las líneas: no afirmar "no hay ninguna conectada" */
  const [linesError, setLinesError] = useState("");
  /** Pestaña activa. La página juntaba 7 secciones en un scroll de ~3000px: para ver la salud
   *  de las líneas había que pasar por delante de todo el embudo. Cada cosa en su lugar. */
  const [tab, setTab] = useState<TabKey>("stats");
  // dentro de su propia pestaña, un acordeón cerrado es un click de más sin motivo
  const [ficha, setFicha] = useState(true);
  /** modal de confirmación: calcula con un dry-run cuántos saldrían antes de dejar mandar */
  const [confirmarEnvio, setConfirmarEnvio] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  // bitácora (lazy: sólo se pide cuando el operador la abre)
  const [bitacora, setBitacora] = useState<BitacoraReal | null>(null);
  const [logAbierto, setLogAbierto] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [logFiltro, setLogFiltro] = useState<string>("");
  /**
   * A cuántos de este análisis les llega WhatsApp oficial: la misma cuenta que el asistente muestra en «Por dónde y
   * cuándo» (`useAlcanceOficial`). Sin esto, «Mandar por WhatsApp oficial» llevaba igual a armar la campaña oficial, y
   * con 0 personas recién fallaba al confirmar («no hay a quién mandarle»), después de la plantilla y la prueba. Sólo se
   * pide cuando el botón se puede usar (con oferta elegida y datos que no son de ejemplo).
   */
  const motivoOficial = motivoNoOficial(campana);
  // sólo para el equipo: armar una campaña oficial no es de una persona de un cliente (no ve el botón)
  // con el reparto QR / API la cuenta es sólo la de la API: se vuelve a pedir cuando cambia
  const alcanceOficial = useAlcanceOficial(equipo && campana && !motivoOficial ? campana.id : null, campana?.reparto?.definidoAt ?? "sin-reparto");
  /** el panel para repartir los WhatsApp entre las líneas por QR y WhatsApp oficial (con reparto se ve siempre) */
  const [verReparto, setVerReparto] = useState(false);
  const oficialSinNadie = equipo && !motivoOficial && alcanceOficial.alcance?.pueden === 0;
  /** con 0, el botón no lleva: abre el porqué (qué dice el archivo del consentimiento y qué hacer) */
  const [verPorQueOficial, setVerPorQueOficial] = useState(false);
  // por qué por las líneas sí le llegó y por el oficial no: depende de la política de la marca (sólo hace falta con 0)
  const politicaOptIn = usePoliticaConsentimiento(oficialSinNadie);
  const porQueCanales = politicaOptIn.cargando ? null : porQueDifieren(politicaOptIn.politica);

  const cargar = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    setError("");
    try {
      const c = await activaApi.campaign(id);
      if (!c?.data) throw new Error("No encontramos esta campaña");
      setCampana(c.data);
      if (c.data.shareToken) setShareUrl(`${window.location.origin}/r/${c.data.shareToken}`);
      // el embudo y las respuestas sólo existen una vez que hubo envío
      if (yaEnvio(c.data.estado)) {
        /**
         * "No hay datos" y "no pude traerlos" NO son lo mismo. Con `.catch(() => null)` a secas,
         * un backend caído dejaba `results` en null y la pantalla pintaba el embudo entero en
         * CERO — con el chip verde "Real". O sea: afirmaba que la campaña no logró nada cuando
         * en realidad no sabíamos nada. Es el pecado del módulo, servido en la pantalla principal.
         */
        const [r, rp] = await Promise.allSettled([activaApi.results(id), activaApi.responses(id)]);
        if (r.status === "fulfilled" && r.value?.data) { setResults(r.value.data); setResultsError(""); }
        else if (r.status === "rejected") setResultsError("No pudimos traer los resultados de esta campaña.");
        if (rp.status === "fulfilled" && rp.value?.data) setResponses(rp.value.data);
      }
      // Ídem con las líneas: si la consulta falla, NO afirmar "no hay ninguna conectada" (eso
      // deshabilita el botón de enviar y culpa al operador de un problema que es nuestro). A una
      // persona de un cliente no se le piden: son del equipo (y GET /lines escribe).
      if (esEquipoDeenex()) {
        const l = await activaApi.lines().catch(() => null);
        if (l?.data) { setLines(l.data); setLinesError(""); }
        else setLinesError("No pudimos verificar el estado de las líneas.");
      }
    } catch (e: any) {
      setError(e?.message || "No pudimos conectar con el servidor. Probá de nuevo en un rato.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  // Mientras despacha, el worker corre en background: refrescamos cada 2s. Sin skeleton:
  // se mantiene el render anterior y sólo cambian los números (nada de layout jump).
  useEffect(() => {
    if (campana?.estado !== "enviando") return;
    const t = setInterval(() => cargar(true), 2000);
    return () => clearInterval(t);
  }, [campana?.estado, cargar]);

  const cargarBitacora = useCallback(async (page = 1, filtro = logFiltro) => {
    setLogLoading(true);
    try {
      const f = filtro ? { estadoEntrega: filtro } : {};
      const r = await activaApi.events(id, { page, limit: 25, ...f });
      if (r?.data) setBitacora(r.data);
    } catch (e: any) {
      setError(e?.message || "No pudimos traer la bitácora");
    } finally {
      setLogLoading(false);
    }
  }, [id, logFiltro]);

  useEffect(() => { if (logAbierto && !bitacora) cargarBitacora(1); }, [logAbierto, bitacora, cargarBitacora]);
  const filtrarLog = (f: string) => { setLogFiltro(f); cargarBitacora(1, f); };

  const accion = async (nombre: string, fn: () => Promise<void>) => {
    setBusy(nombre); setError("");
    try { await fn(); } catch (e: any) { setError(e?.message || "No se pudo completar la acción"); }
    finally { setBusy(""); }
  };

  const doMedir = () => accion("medir", async () => {
    const r = await activaApi.measure(id);
    if (r?.data) setMedicion(r.data);
    await cargar(true);
  });
  // Base de SEGUIMIENTO: los 2 CSV actualizados del período posterior cierran la medición del piloto.
  const [segFiles, setSegFiles] = useState<{ pedidos?: string; clientes?: string }>({});
  const onSegFile = (which: "pedidos" | "clientes") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    void textoDeCsv(f).then((texto) => setSegFiles((prev) => ({ ...prev, [which]: texto })));
  };
  const doSeguimiento = () => accion("seguimiento", async () => {
    if (!segFiles.pedidos || !segFiles.clientes) return;
    const r = await activaApi.cargarSeguimiento(id, { content: segFiles.pedidos, encoding: "csv" }, { content: segFiles.clientes, encoding: "csv" });
    // req() devuelve el 422 sin lanzar: sin cortar acá, los archivos se borraban y la pantalla
    // recargaba como si se hubiera medido. Se muestra el motivo y los archivos elegidos quedan.
    if (r?.success === false) { setError(mensaje422Mapeo(r)); return; }
    if (r?.data) setMedicion(r.data);
    setSegFiles({});
    await cargar(true);
  });
  const doCompartir = () => accion("share", async () => {
    const r = await activaApi.share(id);
    const url = `${window.location.origin}/r/${r.data.token}`;
    setShareUrl(url);
    try { await navigator.clipboard.writeText(url); } catch { /* clipboard opcional */ }
  });
  // Revocar el link (H14). Se confirma ANTES de `accion` para que cancelar no marque busy ni borre
  // el error que estuviera a la vista.
  const doDejarDeCompartir = () => {
    if (!window.confirm("¿Dejar de compartir el reporte? El link actual deja de funcionar para todos los que lo tengan. Si después lo volvés a compartir, se crea un link nuevo.")) return;
    return accion("unshare", async () => {
      await activaApi.unshare(id);
      setShareUrl("");
      await cargar(true);
    });
  };
  /**
   * Este análisis, por WhatsApp oficial: si ya salió una campaña oficial de él, se sigue ésa (cada toque creaba otro
   * borrador con la misma audiencia); si no, crea el borrador allá con sus grupos y sigue en la plantilla.
   */
  const doOficial = () => accion("oficial", async () => {
    // con 0 personas la campaña oficial no podría salir: el botón ya no lleva acá, y esto lo asegura
    if (!campana || oficialSinNadie) return;
    const c = await seguirPorOficial(campana);
    if (!c.ok) { setError(c.error); return; }
    navigate(c.ruta);
  });
  const doEnviar = () => accion("enviar", async () => {
    setConfirmarEnvio(false);
    const r = await activaApi.send(id);
    if (r?.status === 422) { setError(r.error || "No se pudo enviar"); return; }
    await cargar(true);
  });

  /* ── Derivados honestos ───────────────────────────────────────────────── */

  // "Volvieron" sólo vale si la atribución es EXACTA (comparada contra una base del período
  // siguiente). Una campaña puede estar "measured" y no tener base nueva: measure() marca
  // "measured" pase lo que pase. Por eso miramos la atribución, no el estado.
  const atribucion = medicion?.atribucion || results?.atribucion;
  const atribucionPendiente = atribucion !== "exacta-por-snapshot";
  /* Se midió (la campaña queda «Medida») pero no había una base posterior al envío: `POST /campaigns/:id/measure`
     sin seguimiento cargado, o una medición de antes de que existiera la carga del seguimiento. El aviso se dejó de
     mostrar cuando «Intentar medir ahora» pasó a ser la carga de los archivos (7c9529c), y la pantalla quedaba con el
     chip «Medida» arriba y «Todavía no podemos saber quién volvió a comprar» abajo, sin decir por qué. */
  const midioSinBase = atribucion === "pendiente-base-nueva";

  /* El gate ("¿está medido?") y los NÚMEROS ("cuántos volvieron") tienen que salir de la MISMA
     fuente. El gate mira `atribucion`, que combina medicion↔results; pero los valores se leían
     siempre de `results.total`. Si `medicion` dice "exacta-por-snapshot" y `results` no está (o
     su refetch falló), el gate abría con "medido" pero imprimía `results.total.volvieron || 0`
     → un 0 fabricado presentado como medición. Cuando la atribución la fija `medicion`, los
     números salen de `medicion`; si por lo que sea no hay número real, se apaga el gate en vez
     de mostrar un cero medido. */
  const fuenteMedicion = medicion?.atribucion ? medicion : results?.total;
  const volvieronMedidos = fuenteMedicion?.volvieron;
  const plataRecuperadaMedida = fuenteMedicion?.plataRecuperada;
  const hayNumeroMedido = typeof volvieronMedidos === "number";
  // sólo se muestra como medido si la atribución es exacta Y el número realmente llegó
  const mostrarVolvieron = !atribucionPendiente && hayNumeroMedido;

  // procedencia: sale del DATO (campo `transporte` de cada evento), no de la env
  const hayDatosSimulados = !!results?.hayDatosSimulados;
  const eventosSimulados = results?.eventosSimulados || 0;

  const pendientesCupo = campana?.stats?.pendientes || 0;
  const lineasDesconectadas = lines.filter((l) => !l.conectada && l.estado !== "desconectada").length;
  // Si no pudimos consultar las líneas, NO afirmamos que no hay ninguna: eso bloquea el envío y
  // le echa la culpa al operador de un fallo nuestro.
  const sinLineas = !linesError && lines.filter((l) => l.conectada).length === 0;
  /**
   * Qué falta para que lo que salió en modo de prueba salga de verdad. Decía siempre «conectá una línea de WhatsApp y
   * una casilla de email», también con una línea conectada y también a un cliente (las líneas son del equipo: a un
   * cliente ni se le piden, así que de él no se sabe si hay). Ver `comoEnviarDeVerdad`.
   */
  const paraEnviarDeVerdad = hayDatosSimulados
    ? comoEnviarDeVerdad({
        equipo,
        simuladosWa: results?.porCanal ? results.porCanal.WhatsApp?.simulados ?? 0 : undefined,
        simuladosEmail: results?.porCanal ? results.porCanal.Email?.simulados ?? 0 : undefined,
        hayLineaConectada: equipo && !linesError ? lines.some((l) => l.conectada) : null,
      })
    : null;

  /**
   * Cupo consumido HOY por una línea.
   *
   * `enviadosHoy` NO se resetea a medianoche: el backend lo pone en 0 recién cuando despacha y ve
   * que `cupoFecha` cambió. O sea que a la mañana el contador todavía trae el consumo de AYER, y
   * la UI pintaba 180/200 — el operador concluía que no le quedaba cupo y no arrancaba el envío.
   *
   * Se compara como STRING contra el día UTC, igual que el backend: `new Date("2026-07-15")` parsea
   * a medianoche UTC y en UTC-3 `.toDateString()` devolvería el día anterior → daría 0 siempre.
   * Y se refleja el día UTC (no el local) a propósito: el worker decide con UTC, así que mostrar
   * hora local pintaría un cupo distinto del que se aplica.
   */
  const hoyUTC = new Date().toISOString().slice(0, 10);
  const consumidoHoy = (l: LineaReal) => (l.cupoFecha === hoyUTC ? l.enviadosHoy || 0 : 0);

  // pendientes por causa: se destraban en momentos OPUESTOS (cupo → mañana · franja → hoy)
  const sinCupo = campana?.stats?.sinCupo ?? pendientesCupo;
  const fueraDeFranja = campana?.stats?.fueraDeFranja || 0;
  /**
   * H03: mails que el último envío sacó de la cola porque falta FRONTEND_URL (sin link de baja no
   * sale ningún mail). El backend lo persiste en stats; la respuesta de send() se pierde al cerrar
   * el modal, y sin esto la campaña terminaba en «enviada» con mails pendientes y nadie se enteraba.
   */
  const mailsSinLinkBaja = campana?.stats?.emailsSinLinkBaja || 0;
  const franja = campana?.context?.franjaHoraria;

  // ¿alcanza el cupo de hoy para terminar la campaña? Es la única pregunta abierta de "Salud",
  // y hoy el operador la resolvía haciendo la resta a mano, línea por línea.
  const conectadas = lines.filter((l) => l.conectada);
  const capacidadHoy = conectadas.reduce((s, l) => s + Math.max(0, (l.msgsPorDia || 0) - consumidoHoy(l)), 0);
  const topeDiario = conectadas.reduce((s, l) => s + (l.msgsPorDia || 0), 0);
  /**
   * SÓLO la cola por cupo. Usaba `pendientes` (= sinCupo + fueraDeFranja) y entonces afirmaba
   * "Alcanza: los N que faltan salen hoy" sobre mensajes frenados por la VENTANA HORARIA, que el
   * cupo no destraba: si la franja ya cerró, no sale ninguno por más cupo libre que haya.
   * Cada cola se compara contra lo que efectivamente la libera.
   */
  const colaPendiente = sinCupo;
  const alcanzaHoy = colaPendiente > 0 && capacidadHoy >= colaPendiente;
  const diasAlTope = topeDiario > 0 ? Math.ceil(colaPendiente / topeDiario) : 0;

  // El backend sólo pone en porCanal los canales CON eventos: si la campaña fue sólo WhatsApp,
  // porCanal.Email no existe. Se filtra por `total` y NO por `enviados`: un canal donde no salió
  // nada (68 mails encolados, 0 enviados porque falta el SMTP) tiene enviados=0 y quedaría
  // escondido — que es exactamente el caso que hay que ver. Con un solo canal el corte no aporta
  // (el agregado ya es ese canal), así que se omite.
  const porCanales = Object.entries(results?.porCanal || {}).filter(([, c]) => (c?.total ?? c?.enviados ?? 0) > 0);
  /* Reloj que avanza mientras la campaña envía.
     `enviandoHace` medía Date.now() − updatedAt dentro de un useMemo con deps
     [estado, updatedAt]. Pero el backend NO reescribe la campaña durante el envío —updatedAt
     recién cambia al TERMINAR—, así que el useMemo nunca recalculaba: el número quedaba clavado
     en el valor del primer render y `enviandoHace > 30` no se volvía true jamás con la página
     abierta. Un tick propio cada 30s (independiente del poll de datos) lo mantiene vivo. */
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (campana?.estado !== "enviando") return;
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [campana?.estado]);
  const enviandoHace = useMemo(() => {
    if (campana?.estado !== "enviando" || !campana?.updatedAt) return 0;
    return Math.round((Date.now() - new Date(campana.updatedAt).getTime()) / 60000);
    // `tick` entra en deps a propósito: es lo que fuerza el recálculo con el reloj.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campana?.estado, campana?.updatedAt, tick]);

  /**
   * ¿La corrida está ATASCADA, o simplemente es lenta?
   *
   * El aviso salía con `enviandoHace > 30`, o sea por ANTIGÜEDAD. Pero el envío gotea a 30–90s por
   * mensaje (ritmo anti-ban): una campaña de 30 contactos tarda ~30 minutos **por diseño**, y de
   * 183 tarda ~3 horas. El cartel "puede haber quedado colgada" saltaba en TODA campaña normal —
   * y una alarma que suena siempre deja de significar algo.
   *
   * Lo que importa no es cuánto hace que arrancó: es si AVANZA. Los MessageEvent se escriben uno
   * por uno, así que `results.total.enviados` sube en vivo aunque `stats` recién se guarde al
   * final. Sólo se avisa cuando pasó MUCHO más de lo que este volumen debería tardar.
   */
  const enCurso = campana?.estado === "enviando";
  // a cuántos les escribe la corrida (`stats.aEnviar`), no toda la base del análisis (`contactos`): a un grupo de 200 en
  // una base de 3.000 decía «quedan 2.988». Una campaña que salió antes de guardarlo sigue con `contactos`.
  const aEnviar = campana?.stats?.aEnviar ?? campana?.stats?.contactos ?? 0;
  const yaSalieron = results?.total?.enviados || 0;
  const restantes = Math.max(0, aEnviar - yaSalieron);
  // ~60s promedio por mensaje (media de 30–90s); ×2 de margen antes de gritar
  const minutosEsperados = Math.ceil((aEnviar * 60) / 60);
  const pareceColgada = enCurso && aEnviar > 0 && enviandoHace > minutosEsperados * 2 + 10;

  const rep = campana?.report;
  const total = results?.total;
  const enviado = yaEnvio(campana?.estado);
  const enviados = total?.enviados || 0;
  const bajas = total?.bajas ?? responses.filter((r) => r.estado === "baja").length;
  const respondieron = total?.respondieron || 0;

  /* ── Exports ──────────────────────────────────────────────────────────── */

  const notasExport = () => [
    `Campaña: ${campana?.nombre} (${campana?.marca}) · estado: ${estadoInfo(campana?.estado).label}`,
    `Generado: ${new Date().toLocaleString("es-AR")}`,
    ...(hayDatosSimulados
      ? [`ATENCIÓN: ${eventosSimulados} mensajes NO salieron de verdad (modo de prueba, o email sin una casilla conectada). No son entregas reales.`]
      : []),
    atribucionPendiente
      ? 'Atribución: PENDIENTE — todavía no se cargó una base del período siguiente, «volvieron» no está medido.'
      : "Atribución: exacta (comparada contra la base del período siguiente, persona por persona).",
    'La columna «plata» es MEDIDA: lo que cada persona gastó de más después del envío (monto acumulado posterior - monto al momento de la campaña). No es un ticket promedio.',
    "Sólo se cuentan compras de días POSTERIORES al envío: una compra del mismo día es ambigua y no se atribuye.",
  ];

  const exportGrupos = () => descargarCSV(
    `activa_resultados_${slug(campana?.nombre || "")}`,
    results?.porGrupo || [],
    [
      { key: "nombre", label: "Grupo", get: (r) => `${r.emoji} ${r.nombre}` },
      { key: "enviados", label: "Enviados", get: (r) => r.enviados },
      { key: "entregados", label: "Entregados", get: (r) => r.entregados ?? 0 },
      { key: "leidos", label: "Leidos (solo WhatsApp)", get: (r) => r.leidos ?? 0 },
      { key: "respondieron", label: "Respondieron", get: (r) => r.respondieron },
      { key: "volvieron", label: "Volvieron", get: (r) => (atribucionPendiente ? "sin medir" : r.volvieron) },
      { key: "plata", label: "Plata estimada (ARS)", get: (r) => (atribucionPendiente ? "sin medir" : r.plata) },
    ],
    notasExport()
  );

  /* El Excel de segmentación lo arma el BACKEND (una hoja por grupo, más Resumen y bordes), a
     diferencia de los otros exports de esta pantalla que se arman acá con descargarCSV. Es a
     propósito: el CSV y el Excel de una misma campaña tienen que decir lo mismo, y para eso
     salen del mismo lugar. */
  const [bajandoSeg, setBajandoSeg] = useState(false);
  const exportSegmentacion = async () => {
    if (!id) return;
    setBajandoSeg(true);
    // mismo criterio que `accion`: limpiar el error previo antes de intentar de nuevo. No se
    // reusa `accion` entero porque comparte `busy` con TODOS los botones de esta pantalla, y
    // acá sólo tiene que quedar ocupado (y avisar) el botón de la segmentación.
    setError("");
    try { await activaApi.descargarSegmentacionXLSX(id); }
    catch (e) { setError((e as Error)?.message || "No se pudo bajar la segmentación"); }
    finally { setBajandoSeg(false); }
  };

  const exportRespuestas = () => descargarCSV(
    `activa_RESPUESTAS_PII_${slug(campana?.nombre || "")}`,
    responses,
    [
      { key: "persona", label: "Persona", get: (r) => r.persona },
      { key: "nombre", label: "Nombre", get: (r) => r.nombre || "" },
      { key: "contacto", label: "Contacto (enmascarado)", get: (r) => r.contactoVisible || "" },
      { key: "grupo", label: "Grupo", get: (r) => r.grupo },
      { key: "estado", label: "Estado", get: (r) => r.estado },
      { key: "respuesta", label: "Respuesta", get: (r) => r.respuesta },
      { key: "mensajeEnviado", label: "Mensaje enviado", get: (r) => r.mensajeEnviado },
      { key: "linea", label: "Línea", get: (r) => r.lineaNombre || r.linea },
      { key: "createdAt", label: "Fecha", get: (r) => r.createdAt },
    ],
    [...notasExport(), "ATENCIÓN: este archivo tiene datos personales de clientes. No lo reenvíes ni lo dejes en una carpeta compartida."]
  );

  const exportBitacora = () => descargarCSV(
    `activa_BITACORA_PII_${slug(campana?.nombre || "")}`,
    bitacora?.items || [],
    [
      { key: "personaId", label: "Persona", get: (e) => e.personaId },
      { key: "grupo", label: "Grupo", get: (e) => e.grupo },
      { key: "canal", label: "Canal", get: (e) => e.canal },
      { key: "numeroDestino", label: "Destino (enmascarado)", get: (e) => e.numeroDestino },
      { key: "lineaOrigen", label: "Linea origen", get: (e) => e.lineaOrigen },
      { key: "estadoEntrega", label: "Estado entrega", get: (e) => e.estadoEntrega || "" },
      { key: "motivoError", label: "Motivo del error", get: (e) => e.motivoError || "" },
      { key: "intentos", label: "Intentos", get: (e) => e.intentos ?? "" },
      { key: "entregadoAt", label: "Entregado el", get: (e) => e.entregadoAt || "" },
      { key: "leidoAt", label: "Leido el", get: (e) => e.leidoAt || "" },
      { key: "respondio", label: "Respondio", get: (e) => e.respondio },
      { key: "respuesta", label: "Respuesta", get: (e) => e.respuesta || "" },
      { key: "baja", label: "Pidio baja", get: (e) => e.baja },
      { key: "volvioAComprar", label: "Volvio a comprar", get: (e) => e.volvioAComprar },
      { key: "createdAt", label: "Fecha", get: (e) => e.createdAt },
    ],
    [
      ...notasExport(),
      `Bitacora: pagina ${bitacora?.page} de ${bitacora?.paginas} (${bitacora?.items.length} de ${bitacora?.total} mensajes).`,
      "ATENCIÓN: tiene datos de clientes, aunque los contactos van enmascarados.",
    ]
  );

  /* ── Render ───────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="min-h-screen p-6 md:p-8 flex items-center justify-center">
        <div className="text-center"><Loader2 size={28} className="animate-spin text-violet-600 mx-auto" /><p className="text-sm text-slate-500 mt-3">Cargando la campaña…</p></div>
      </div>
    );
  }

  if (!campana) {
    return (
      <div className="min-h-screen p-6 md:p-8">
        <div className="mx-auto max-w-6xl space-y-4">
          <Link to="/activa" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600"><ArrowLeft size={14} /> Campañas</Link>
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700 flex items-start gap-2"><AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />{error || "No encontramos esta campaña."}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-6xl space-y-8">
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div>
          <Link to="/activa" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 mb-4"><ArrowLeft size={14} /> Campañas</Link>

          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">Campaña por archivo</span>
                <ChipEstado estado={campana.estado} />
              </div>
              <h1 className="text-[28px] leading-tight font-semibold text-slate-900 mt-2 tracking-tight truncate">{campana.nombre}</h1>
              <div className="flex items-center gap-3 flex-wrap text-xs text-slate-400 mt-1.5">
                {/* El hash del archivo ayuda a soporte (dos cargas del mismo archivo) pero no le dice nada al operador:
                    queda en el title de la fecha, no a la vista. */}
                <span className="inline-flex items-center gap-1" title={campana.fileHash ? `Archivo #${campana.fileHash}` : undefined}><Clock size={12} /> Creada el {fecha(campana.createdAt)}</span>
                {/* El período es el mes en que se analizó, no el de los datos: «Datos de octubre» a un archivo que
                    llega hasta julio contradecía al reporte de abajo. Lo que importa es hasta cuándo hay ventas. */}
                {campana.report?.fechaCorte && <><span>·</span><span>Ventas hasta el {fechaDia(campana.report.fechaCorte)}</span></>}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => cargar()} disabled={!!busy} className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
                <RefreshCw size={13} /> Actualizar
              </button>
              {/* Vive en el header (no adentro de "Resultado por grupo") a propósito: la
                  segmentación existe desde que la campaña se ANALIZA, mucho antes de que haya
                  resultados de envío — y es justo antes de mandar los mensajes cuando auditarla
                  sirve. Atarla a `results?.porGrupo` la escondía exactamente cuando más hacía
                  falta. Acá arriba se ve sin importar la pestaña ni si ya se envió algo. */}
              <button
                onClick={exportSegmentacion}
                disabled={bajandoSeg}
                title="Excel con una hoja por grupo: quiénes están en cada uno y por qué"
                className="h-9 px-3 rounded-lg border border-violet-200 bg-white text-xs font-semibold text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Download size={13} /> {bajandoSeg ? "Armando…" : "Segmentación (Excel)"}
              </button>
              {equipo && (
                <button onClick={doCompartir} disabled={!!busy} className="h-9 px-3 rounded-lg border border-violet-200 bg-white text-xs font-semibold text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1.5 disabled:opacity-50">
                  {busy === "share" ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />} Compartir reporte
                </button>
              )}
              {/* El mismo análisis, por el canal oficial (Twilio). Sin oferta elegida o con datos de ejemplo no se
                  ofrece: el motivo queda en el title, y en el asistente se elige la oferta. Dice a cuántos les
                  llegaría; con 0 no lleva a armar una campaña vacía: abre el porqué (como «Por dónde y cuándo»). Armar una
                  campaña es del equipo: a una persona de un cliente no se le ofrece. */}
              {!equipo ? null : oficialSinNadie ? (
                <button type="button" onClick={() => setVerPorQueOficial((v) => !v)} aria-expanded={verPorQueOficial} aria-controls="alcance-oficial-detalle"
                  title="Por WhatsApp oficial no le podés escribir a nadie de este análisis: tocá para ver por qué y qué hacer"
                  className="h-9 px-3 rounded-lg border border-amber-300 bg-amber-50 text-xs font-semibold text-amber-900 hover:bg-amber-100 inline-flex items-center gap-1.5">
                  <AlertTriangle size={13} /> WhatsApp oficial: no le llega a nadie
                </button>
              ) : (
                <button onClick={doOficial} disabled={!!busy || !!motivoOficial}
                  title={motivoOficial || (alcanceOficial.alcance
                    ? `Por WhatsApp oficial le llega a hasta ${num(alcanceOficial.alcance.pueden)} de ${num(alcanceOficial.alcance.clientes)} ${campana?.reparto ? "asignados a la API" : "clientes"}. Crea una campaña con los grupos de este análisis`
                    : "Crea una campaña de WhatsApp oficial con los grupos de este análisis")}
                  className="h-9 px-3 rounded-lg bg-violet-600 text-xs font-semibold text-white hover:bg-violet-700 inline-flex items-center gap-1.5 disabled:opacity-40">
                  {busy === "oficial" ? <Loader2 size={13} className="animate-spin" /> : <BadgeCheck size={13} />} {campana?.reparto ? "Mandar la parte de la API" : "Mandar por WhatsApp oficial"}
                  {!motivoOficial && alcanceOficial.alcance && <span className="font-normal opacity-90">· hasta {num(alcanceOficial.alcance.pueden)}</span>}
                </button>
              )}
              {/* Repartir entre QR y API: del equipo, y no con el dataset de ejemplo (no sale por ninguna vía) */}
              {equipo && campana && !campana.esEjemplo && !campana.reparto && (
                <button type="button" onClick={() => setVerReparto((v) => !v)} aria-expanded={verReparto} aria-controls="reparto-detalle"
                  className="h-9 px-3 rounded-lg border border-violet-200 bg-white text-xs font-semibold text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1.5">
                  <Split size={13} /> Repartir QR / API
                </button>
              )}
            </div>
          </div>

          {oficialSinNadie && verPorQueOficial && (
            <div id="alcance-oficial-detalle" className="mt-4">
              <AlcanceOficial estado={alcanceOficial} porQue={porQueCanales} />
            </div>
          )}

          {campana && !campana.esEjemplo && (campana.reparto || (equipo && verReparto)) && (
            <div id="reparto-detalle" className="mt-4">
              <RepartoWhatsApp
                campaignId={campana.id}
                puedeEditar={equipo}
                onCambio={(e) => setCampana((c) => (c ? { ...c, reparto: e.reparto } : c))}
                onAbrirApi={motivoOficial || oficialSinNadie ? undefined : doOficial}
                abriendoApi={busy === "oficial"}
              />
            </div>
          )}

          {shareUrl && (
            <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50/50 p-3">
              <div className="flex items-center gap-2">
                <input readOnly value={shareUrl} onFocus={(e) => e.currentTarget.select()} className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-mono text-slate-700" />
                <a href={shareUrl} target="_blank" rel="noreferrer" className="rounded-lg bg-violet-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-violet-700 whitespace-nowrap">Abrir</a>
              </div>
              <div className="flex items-center justify-between gap-2 mt-1.5">
                <p className="text-[11px] text-slate-400">Link público (sin login), sólo agregados, sin datos personales. Si lo dejás de compartir, el link viejo deja de funcionar.</p>
                {equipo && (
                  <button onClick={doDejarDeCompartir} disabled={!!busy} className="shrink-0 text-[11px] font-semibold text-slate-500 hover:text-red-600 inline-flex items-center gap-1 disabled:opacity-50">
                    {busy === "unshare" && <Loader2 size={11} className="animate-spin" />} Dejar de compartir
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Sin el nombre del comercio (las campañas que quedaron firmadas «Tu marca»), el envío no sale (SIN_MARCA)
              y «Reanudar» diría el motivo sin dónde arreglarlo: el asistente no abre una campaña que ya salió. Se
              completa acá; los mensajes que faltan salen con el nombre nuevo (QA del 08/10). Es armar la campaña
              (POST /campaigns/:id/marca): del equipo. */}
          {equipo && !marcaQueFirma(campana.marca) && campana.estado !== "measured" && (
            <div className="mt-4">
              <FirmaComercio campaignId={campana.id || id || ""} marca={campana.marca} onGuardada={(c) => setCampana(c)} />
            </div>
          )}
        </div>

        {/* ── Pestañas ─────────────────────────────────────────────────── */}
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={equipo ? TABS : TABS.filter((t) => t.key !== "salud")}
          alerta={{
            // un problema no puede quedar escondido detrás de una pestaña cerrada
            salud: lineasDesconectadas > 0 || sinLineas,
            stats: hayDatosSimulados || pendientesCupo > 0,
          }}
        />

        {/* El panel al que apuntan los aria-controls de las pestañas. El contenido vive en
            bloques condicionales dispersos, así que el panel los envuelve a todos y cambia de
            identidad con la pestaña activa (`key` fuerza el remount → el lector de pantalla
            anuncia el panel nuevo en vez de quedarse con el viejo). */}
        <div
          key={tab}
          id={`panel-${tab}`}
          role="tabpanel"
          aria-labelledby={`tab-${tab}`}
          tabIndex={0}
          className="space-y-8 focus:outline-none"
        >

        {/* ── 1 · Las seis métricas ──────────────────────────────────────
            Con resultsError NO se renderizan: mostrar ceros cuando el fetch falló es afirmar que
            la campaña no logró nada. El cartel rojo de arriba ya explica qué pasó. */}
        {tab === "stats" && !resultsError && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <StatTile
            icon={<Users size={15} />} label="Contactos" tono="neutro"
            value={num(campana.stats?.contactos || 0)}
            sub={`${(campana.grupos || []).length} ${plural((campana.grupos || []).length, "grupo", "grupos")}`}
          />
          <StatTile
            icon={<CheckCheck size={15} />} label="Entregados" tono="entrega"
            value={enviado ? num(total?.entregados || 0) : null}
            vacio="todavía no se envió"
            sub={`de ${num(enviados)} enviados`}
            meterPct={pct(total?.entregados || 0, enviados)}
            chip={hayDatosSimulados ? CHIP.simulado : undefined}
          />
          <StatTile
            icon={<CheckCheck size={15} />} label="Leídos" tono="lectura"
            value={enviado ? num(total?.leidos || 0) : null}
            vacio="todavía no se envió"
            sub="✓✓ azul · sólo WhatsApp"
            meterPct={pct(total?.leidos || 0, enviados)}
          />
          <StatTile
            icon={<MessageSquare size={15} />} label="Respondieron" tono="respuesta"
            value={enviado ? num(respondieron) : null}
            vacio="todavía no se envió"
            sub={`${pct(respondieron, enviados)}% de los enviados`}
            meterPct={pct(respondieron, enviados)}
          />
          <StatTile
            icon={<Ban size={15} />} label="Pidieron baja" tono="baja"
            value={enviado ? num(bajas) : null}
            vacio="todavía no se envió"
            sub={bajas > 0 ? "no se los contacta más" : "nadie se dio de baja"}
            meterPct={pct(bajas, enviados)}
          />
          <StatTile
            icon={<TrendingUp size={15} />} label="Volvieron a comprar" tono="resultado"
            value={mostrarVolvieron ? num(volvieronMedidos!) : null}
            vacio="falta la base del mes siguiente"
            /* lo que compraron después del envío, sin grupo de control: no es «recuperado» (decía eso hasta el 29/10) */
            sub={mostrarVolvieron ? `${compact(plataRecuperadaMedida || 0)} en compras` : undefined}
            chip={mostrarVolvieron ? CHIP.medido : CHIP.sinMedir}
          />
        </div>
        )}

        {/* ── 2 · Avisos ── FUERA de las pestañas: un error o un "esto es simulado" tiene que
            verse desde donde estés, no sólo si abrís la pestaña correcta. ─────────────────── */}
        {(hayDatosSimulados || pendientesCupo > 0 || mailsSinLinkBaja > 0 || lineasDesconectadas > 0 || enviandoHace > 30 || error || resultsError || linesError) && (
          <div className="space-y-2">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3.5 text-sm text-red-700 flex items-start gap-2.5">
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />{error}
              </div>
            )}
            {/* "no pudimos traer los datos" ≠ "la campaña no logró nada". Antes esto era un
                embudo entero en cero con el chip verde "Real". */}
            {resultsError && (
              <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 flex items-start gap-3">
                <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-red-900">{resultsError}</p>
                  <p className="text-xs text-red-800 mt-1">Los números de abajo NO están disponibles — no son cero, no los pudimos leer. Probá "Actualizar".</p>
                </div>
              </div>
            )}
            {linesError && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 flex items-start gap-2.5">
                <Smartphone size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">{linesError} No podemos confirmar si hay líneas listas para enviar.</p>
              </div>
            )}
            {/* lo primero: si estos números no salieron de un transporte real, decirlo */}
            {hayDatosSimulados && (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
                <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-amber-900">Ojo: {num(eventosSimulados)} de estos mensajes NO salieron de verdad</p>
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                    Salieron en <strong>modo de prueba</strong> (WhatsApp en modo prueba, o email sin una casilla
                    conectada). Los números sirven para ver que el circuito funciona, pero <strong>no son entregas a
                    clientes reales</strong>.{paraEnviarDeVerdad && <> {paraEnviarDeVerdad}</>}
                  </p>
                </div>
              </div>
            )}
            {pendientesCupo > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 flex items-start gap-2.5">
                <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">{num(pendientesCupo)} {plural(pendientesCupo, "mensaje quedó", "mensajes quedaron")} sin enviar</p>
                  {/* La causa se DERIVA de stats, no se hardcodea: decía "se agotó el cupo diario"
                      también cuando el cupo estaba al 0% y lo que frenó fue la ventana horaria —
                      y el operador esperaba a mañana algo que se destrababa esa misma tarde. */}
                  <p className="text-xs text-amber-700 mt-0.5">
                    {sinCupo > 0 && fueraDeFranja > 0
                      ? `${num(sinCupo)} por cupo diario agotado (se renueva mañana) y ${num(fueraDeFranja)} porque estaba cerrada la ventana de envío${franja ? ` (${franja.desde}–${franja.hasta})` : ""}. No salen solos: cuando se puedan mandar, ${reanudar}.`
                      : fueraDeFranja > 0
                        ? `Estaba cerrada la ventana de envío${franja ? ` (${franja.desde}–${franja.hasta})` : ""}. No salen solos: cuando vuelva a abrir, ${reanudar}.`
                        : `Se agotó el cupo diario de las líneas. El cupo se renueva cada día, pero el envío no sigue solo: cuando haya cupo, ${reanudar}.`}
                  </p>
                </div>
              </div>
            )}
            {mailsSinLinkBaja > 0 && (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
                <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-amber-900">
                    {num(mailsSinLinkBaja)} {plural(mailsSinLinkBaja, "mail no salió", "mails no salieron")}: no se pudo armar el link de baja
                  </p>
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                    {/* Cada uno en sus palabras (QA del 08/10): al comercio, que no sabe qué es FRONTEND_URL ni lo puede
                        resolver, a quién avisarle; al equipo, que es a quien se lo decía ese «avisale al equipo», qué
                        variable falta (la misma que nombra MOTIVO_SIN_FRONTEND_URL en las respuestas del envío). */}
                    Los WhatsApp salieron igual.{" "}
                    {equipo
                      ? "Falta configurar FRONTEND_URL en el servidor (la dirección pública de la app): sin ella no se arma el link de baja de cada mail."
                      : "Falta una configuración del sistema: avisale al equipo de Deenex."}
                    {" "}Cuando esté, {equipo ? "reanudá el envío" : "el equipo de Deenex lo reanuda"}: los mails
                    pendientes salen sin gastar intentos y a quien ya recibió no se le vuelve a escribir.
                  </p>
                </div>
              </div>
            )}
            {/* En curso y a ritmo normal: se informa el PROGRESO, no se alarma. Tardar es el
                diseño (30–90s por mensaje para no quemar la línea). */}
            {enCurso && !pareceColgada && aEnviar > 0 && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3.5 flex items-start gap-2.5">
                <Loader2 size={16} className="text-sky-600 flex-shrink-0 mt-0.5 animate-spin" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-sky-900">
                    Enviando · {num(yaSalieron)} de {num(aEnviar)}
                  </p>
                  <p className="text-xs text-sky-700 mt-0.5">
                    Sale goteando para no quemar la línea (30 a 90 segundos entre mensajes)
                    {restantes > 0 && <> · quedan ~{Math.ceil(restantes / 60)} h para los {num(restantes)} restantes</>}.
                  </p>
                </div>
              </div>
            )}
            {/* Sólo acá se sugiere que algo salió mal: pasó MUCHO más de lo que este volumen
                debería tardar. Antes esto saltaba en cualquier campaña de más de 30 mensajes. */}
            {pareceColgada && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 flex items-start gap-2.5">
                <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">
                    Enviando hace {enviandoHace} min y van {num(yaSalieron)} de {num(aEnviar)} — más de lo esperado
                  </p>
                  <p className="text-xs text-amber-800 mt-0.5">Si el sistema se reinició a mitad, la campaña puede haber quedado colgada. Reanudar es seguro: nunca se reenvía a quien ya recibió.</p>
                </div>
              </div>
            )}
            {lineasDesconectadas > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50/60 p-3.5 flex items-start gap-2.5">
                <Smartphone size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800"><strong>{lineasDesconectadas} {plural(lineasDesconectadas, "línea de WhatsApp desconectada", "líneas de WhatsApp desconectadas")}.</strong> Los envíos por esas líneas van a fallar. <Link to="/activa/whatsapp" className="underline">Reconectar</Link></p>
              </div>
            )}
          </div>
        )}

        {(tab === "stats" || tab === "mensajes") && !enviado ? (
          <div className="rounded-2xl border-2 border-dashed border-violet-200 bg-gradient-to-br from-violet-50/60 to-white p-10 text-center">
            <div className="w-12 h-12 rounded-2xl bg-violet-600 text-white flex items-center justify-center mx-auto"><Send size={22} /></div>
            <p className="text-lg font-semibold text-slate-900 mt-3">Esta campaña todavía no se envió</p>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Cuando salgan los mensajes vas a ver acá el embudo de entrega real, las respuestas y la medición.</p>
            {/* Armarla es del equipo: el asistente (/activa/real) es una página que una persona de un cliente no ve. */}
            {equipo && (
              <Link to={`/activa/real?campana=${encodeURIComponent(campana.id || id || "")}`} className="inline-flex items-center gap-2 h-11 px-5 mt-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">Seguir armando la campaña <ArrowLeft size={15} className="rotate-180" /></Link>
            )}
          </div>
        ) : (
          <>
            {/* Estadísticas: el embudo y el corte por grupo */}
            {tab === "stats" && !resultsError && (<>
            {/* ── 3 · Estado de entrega ─────────────────────────────────── */}
            <DeliveryFunnel total={total} atribucionPendiente={atribucionPendiente} simulado={hayDatosSimulados} />

            {/* ── 3.b · El mismo embudo, SEPARADO POR CANAL ──────────────────────────────────
                Promediar WhatsApp y Email miente: en email NO existe el "leído" (no hay recibo),
                así que "Leídos 40 de 1000 · 4%" se lee como "el mensaje no engancha" cuando en
                realidad 700 de esos 1000 eran email y sobre WhatsApp el real es 13%. Además acá
                se ve de una si el SMTP está rechazando todo (Email: 300 enviados / 0 entregados)
                y se corta la corrida antes de quemar la base entera. */}
            {porCanales.length > 1 && (
              <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <SecTitulo titulo="Por canal" sub="WhatsApp y Email no se pueden promediar: no miden lo mismo" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {porCanales.map(([canal, c]) => {
                    const esWa = canal.includes("WhatsApp");
                    // el denominador es lo que HABÍA en el canal, no lo que salió: si salieron 0
                    // de 68, dividir por los enviados daría 0/0 y taparía justo el problema
                    const base = c.total ?? c.enviados ?? 0;
                    const noSalioNada = (c.enviados || 0) === 0 && base > 0;
                    const filas = [
                      { k: "Encolados", v: base, bar: "bg-slate-200" },
                      { k: "Salieron", v: c.enviados, bar: "bg-slate-400" },
                      { k: esWa ? "Entregados ✓✓" : "Aceptados por tu casilla", v: c.entregados, bar: "bg-slate-500" },
                      ...(esWa ? [{ k: "Leídos ✓✓", v: c.leidos, bar: "bg-sky-400" }] : []),
                      { k: "Respondieron", v: c.respondieron, bar: "bg-violet-400" },
                      { k: "No llegaron", v: c.rebotes, bar: "bg-red-400" },
                    ];
                    return (
                      <div key={canal} className={`rounded-xl border p-4 ${noSalioNada ? "border-amber-200 bg-amber-50/40" : "border-slate-100"}`}>
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-3">
                          {esWa ? <Smartphone size={12} /> : <MessageSquare size={12} />} {canal}
                          {!!c.simulados && <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 normal-case tracking-normal">{num(c.simulados)} no salieron</span>}
                        </p>
                        {noSalioNada && (() => {
                          /* La causa se RAMIFICA por el dato. Antes afirmaba "falta configurar el
                             SMTP" siempre — también cuando el SMTP estaba configurado y rechazando
                             todo, que es un problema distinto y con otra solución. Si no tenemos
                             el dato para afirmar la causa, no la afirmamos: mandamos a la bitácora. */
                          const sinConfigurar = (c.simulados || 0) > 0;
                          const rechazando = !sinConfigurar && (c.rebotes || 0) > 0;
                          return (
                            <p className="text-[11px] text-amber-800 mb-3 leading-relaxed">
                              <strong>No salió ni uno.</strong>{" "}
                              {sinConfigurar
                                ? (esWa ? "La línea está en modo prueba: no hay sesión real de WhatsApp." : "No hay una casilla de email conectada: los mails se registran pero no se envían.")
                                : rechazando
                                  ? (esWa ? `Los ${num(c.rebotes)} envíos fallaron. El motivo está en la bitácora.` : `El servidor de email rechazó los ${num(c.rebotes)}. El motivo está en la bitácora.`)
                                  : "Revisá la bitácora para ver qué pasó con cada uno."}
                            </p>
                          );
                        })()}
                        {/* En mobile el label va ARRIBA de la barra. Con todo en una fila, el
                            label (144px) + el valor (64px) se comían 208 de 243px y la barra
                            quedaba en 19px — el 8% del ancho: un gráfico que no comunica nada. */}
                        <div className="space-y-2.5">
                          {filas.map((f) => (
                            <div key={f.k} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                              <div className="flex items-baseline justify-between gap-2 sm:w-36 sm:flex-shrink-0">
                                <span className="text-[11px] text-slate-600 leading-tight">{f.k}</span>
                                {/* en mobile el valor viaja con el label; en desktop vive a la derecha */}
                                <span className="sm:hidden text-[11px] tabular-nums font-semibold text-slate-700">
                                  {num(f.v)} <span className="text-slate-400 font-normal">{pct(f.v, base)}%</span>
                                </span>
                              </div>
                              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                                <div className={`h-full rounded-full ${f.bar}`} style={{ width: `${Math.max(2, pct(f.v, base))}%` }} />
                              </div>
                              <span className="hidden sm:block w-16 text-right text-[11px] tabular-nums font-semibold text-slate-700 flex-shrink-0">
                                {num(f.v)} <span className="text-slate-400 font-normal">{pct(f.v, base)}%</span>
                              </span>
                            </div>
                          ))}
                        </div>
                        {!esWa && (
                          <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">
                            En email no hay "leído": tu casilla sólo confirma que lo <strong>aceptó</strong>, no que llegó a la bandeja de entrada.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* atribución: el detalle que el tile no puede contar. Se usa `mostrarVolvieron`
                (no `atribucionPendiente`) para que el bloque verde "Los dos números son medidos"
                no aparezca cuando la atribución es exacta pero el número real no llegó. */}
            {!mostrarVolvieron ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 text-slate-400 flex items-center justify-center flex-shrink-0"><TrendingUp size={18} /></div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">Todavía no podemos saber quién volvió a comprar</p>
                  {midioSinBase && (
                    <p className="text-sm text-slate-700 mt-0.5 leading-relaxed" data-testid="medido-sin-base">
                      <strong>Ya se midió, pero sin una base posterior al envío:</strong> sin las compras de después, que nadie
                      figure como que volvió no quiere decir nada.
                    </p>
                  )}
                  <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
                    Subí los 2 archivos (pedidos y clientes) <strong>actualizados y completos</strong> del comercio (todos los pedidos, con las compras nuevas). Comparamos persona por persona quién compró después del envío y cuánto gastó.
                  </p>
                  {equipo ? (<>
                  <div className="grid grid-cols-2 gap-2.5 mt-3 max-w-md">
                    {([["pedidos", "Pedidos actualizados"], ["clientes", "Clientes actualizados"]] as const).map(([k, label]) => (
                      <label key={k} className={`rounded-xl border px-3 py-2.5 text-center cursor-pointer transition-colors text-xs font-semibold ${segFiles[k] ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600 hover:border-violet-300"}`}>
                        <input type="file" accept=".csv" className="hidden" onChange={onSegFile(k)} />
                        {segFiles[k] ? "✓ " : ""}{label}
                      </label>
                    ))}
                  </div>
                  <button onClick={doSeguimiento} disabled={!!busy || !segFiles.pedidos || !segFiles.clientes} className="mt-2.5 h-9 px-3.5 rounded-lg bg-violet-600 text-white text-xs font-bold hover:bg-violet-700 inline-flex items-center gap-1.5 disabled:opacity-40">
                    {busy === "seguimiento" ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />} Medir el resultado
                  </button>
                  </>) : (
                    <p className="text-xs text-slate-500 mt-2">La medición la carga el equipo de Deenex: cuando esté, la vas a ver acá.</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5">
                <p className="text-sm text-slate-600 leading-relaxed">
                  <strong className="text-emerald-800">{num(volvieronMedidos ?? 0)} {(volvieronMedidos ?? 0) === 1 ? "persona volvió" : "personas volvieron"} a comprar</strong> después del envío, y {(volvieronMedidos ?? 0) === 1 ? "gastó" : "gastaron"}{" "}
                  <strong className="text-emerald-800">{compact(plataRecuperadaMedida || 0)}</strong>. Los dos números son <strong>medidos</strong>, no estimados:
                  comparamos persona por persona contra la base anterior, y la plata es exactamente lo que gastaron desde el envío.
                </p>
                {/* Medido no es «gracias a la campaña»: no hay grupo de control (decisión de Alan del 29/10, a la vista). */}
                <p data-testid="aclaracion-sin-control" className="text-xs text-slate-600 mt-2 leading-relaxed">
                  Incluye a quienes habrían comprado igual: esta campaña no se compara contra un grupo que no la recibió.
                </p>
                <p className="text-[11px] text-slate-500 mt-2.5 pt-2.5 border-t border-emerald-200/60 leading-relaxed">
                  Sólo contamos compras de días <strong>posteriores</strong> al envío: una compra del mismo día es ambigua (no sabemos si fue antes o
                  después de ver el mensaje) y preferimos quedarnos cortos antes que inflar el resultado.
                  {(medicion?.sinBaseComparable || 0) > 0 && <> {medicion?.sinBaseComparable} persona/s volvieron pero no tenían base previa para calcular cuánto gastaron: no suman plata.</>}
                  {medicion?.fechaEnvioAproximada && <> <strong className="text-amber-700">Ojo:</strong> esta campaña no registró la fecha exacta de envío, se usó la del análisis.</>}
                </p>
              </div>
            )}

            {/* ── 4 · Resultado por grupo ───────────────────────────────── */}
            {results?.porGrupo?.length ? (
              <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <SecTitulo
                  titulo="Resultado por grupo"
                  sub="El corte más accionable: qué grupo responde y cuál no"
                  right={
                    // La segmentación (Excel) se movió al header: existe desde el análisis, no
                    // desde el envío, y acá abajo sólo se renderiza con resultados de envío. El
                    // CSV sí depende de esos resultados (métricas de entrega), así que se queda.
                    <button onClick={exportGrupos} className="h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-[11px] font-semibold text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5">
                      <Download size={12} /> CSV
                    </button>
                  }
                />
                <div className="overflow-x-auto -mx-1">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wider text-slate-400 text-left border-b border-slate-100">
                        <th className="px-3 py-2.5 font-semibold">Grupo</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Enviados</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Entregados</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Leídos</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Respondieron</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Volvieron</th>
                        <th className="px-3 py-2.5 font-semibold text-right text-slate-300">Plata est.</th>
                      </tr>
                    </thead>
                    {/* tabular-nums SÍ va acá: son columnas que tienen que alinear vertical */}
                    <tbody className="divide-y divide-slate-50 tabular-nums">
                      {results.porGrupo.map((r) => (
                        <tr key={r.grupoId} className="hover:bg-slate-50/50">
                          <td className="px-3 py-3 font-medium text-slate-800 whitespace-nowrap">{r.emoji} {r.nombre}</td>
                          <td className="px-3 py-3 text-right text-slate-700">{num(r.enviados)}</td>
                          <td className="px-3 py-3 text-right text-slate-600">{num(r.entregados ?? 0)}</td>
                          <td className="px-3 py-3 text-right text-sky-700 font-medium">{num(r.leidos ?? 0)}<span className="text-[10px] text-slate-300 ml-1">{pct(r.leidos ?? 0, r.enviados)}%</span></td>
                          <td className="px-3 py-3 text-right text-violet-700">{num(r.respondieron)}</td>
                          <td className="px-3 py-3 text-right font-medium text-emerald-700">{atribucionPendiente ? <span className="text-[10px] font-bold uppercase text-slate-400">sin medir</span> : num(r.volvieron)}</td>
                          <td className="px-3 py-3 text-right text-slate-400">{atribucionPendiente ? "—" : compact(r.plata)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            </>)}

            {tab === "mensajes" && (<>
            {/* ── 5 · Respuestas de los clientes ────────────────────────── */}
            <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
              <SecTitulo
                titulo="Respuestas de los clientes"
                sub="Lista de lectura: se contestan desde el WhatsApp del local"
                right={responses.length > 0 ? (
                  <button onClick={exportRespuestas} className="h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-[11px] font-semibold text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5">
                    <Download size={12} /> CSV
                  </button>
                ) : undefined}
              />

              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { l: "Respuestas", v: num(responses.length), c: "text-violet-700" },
                  { l: "Tasa de respuesta", v: `${pct(respondieron, enviados)}%`, c: "text-slate-900" },
                  { l: "Pidieron baja", v: num(bajas), c: bajas > 0 ? "text-red-700" : "text-slate-900" },
                ].map((m) => (
                  <div key={m.l} className="rounded-xl bg-slate-50 p-3">
                    <p className={`text-xl font-semibold ${m.c}`}>{m.v}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{m.l}</p>
                  </div>
                ))}
              </div>

              {responses.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">Todavía nadie respondió a esta campaña.</p>
              ) : (
                <div className="space-y-2.5">
                  {responses.slice(0, 8).map((r, i) => (
                    <div key={r.id || i} className="rounded-xl border border-slate-100 bg-slate-50/40 p-3.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-800 truncate">{r.nombre || "Sin nombre"}{r.contactoVisible ? <span className="font-normal text-slate-500"> · {r.contactoVisible}</span> : null}</span>
                        {/* El badge se DERIVA del dato, no del campo `estado`: hay conversaciones
                            viejas guardadas como "sin responder" sobre un texto que la persona sí
                            mandó (bug del backend, ya arreglado). Si hay respuesta, respondió. */}
                        {(() => {
                          const esBaja = r.estado === "baja";
                          const contesto = !!r.respuesta?.trim();
                          const cls = esBaja ? "bg-red-50 text-red-600" : contesto ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-500";
                          const txt = esBaja ? "pidió baja" : contesto ? "respondió" : "sin responder";
                          return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${cls}`}>{txt}</span>;
                        })()}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">"{r.respuesta}"</p>
                      {/* sólo lo que existe de verdad: campana/oferta vienen vacíos del backend */}
                      <p className="text-[11px] text-slate-400 mt-1.5">{[r.grupo, r.lineaNombre && `por ${r.lineaNombre}`, r.createdAt && fechaHora(r.createdAt)].filter(Boolean).join(" · ") || "—"}</p>
                    </div>
                  ))}
                  {responses.length > 8 && <p className="text-xs text-slate-400 text-center pt-1">y {responses.length - 8} más — bajá el CSV para verlas todas</p>}
                  <p className="text-[11px] text-slate-400 pt-1 border-t border-slate-100">Quien responde BAJA por WhatsApp o se da de baja desde el link del mail queda dado de baja solo. Las bajas que lleguen por otro medio se cargan a mano en Bajas.</p>
                </div>
              )}
            </section>

            </>)}

            {tab === "salud" && (<>
            {/* ── ¿Alcanza el cupo para terminar? — la única pregunta abierta de esta pestaña ── */}
            {colaPendiente > 0 && (
              <section className={`rounded-2xl border p-5 ${alcanzaHoy ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/50"}`}>
                <div className="flex items-start gap-3.5">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${alcanzaHoy ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    <Activity size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-bold ${alcanzaHoy ? "text-emerald-900" : "text-amber-900"}`}>
                      {alcanzaHoy
                        ? `Alcanza: los ${num(colaPendiente)} que faltan salen hoy`
                        : `No alcanza para hoy: faltan ${num(colaPendiente)} y hay cupo para ${num(capacidadHoy)}`}
                    </p>
                    <p className={`text-xs mt-1 leading-relaxed ${alcanzaHoy ? "text-emerald-700" : "text-amber-800"}`}>
                      Cupo libre hoy: <strong>{num(capacidadHoy)}</strong> de {num(topeDiario)} entre {num(conectadas.length)} {plural(conectadas.length, "línea conectada", "líneas conectadas")}.
                      {!alcanzaHoy && topeDiario > 0 && <> Al ritmo actual la campaña termina en <strong>{diasAlTope} día(s)</strong>. Para acelerar: conectá otra línea o subí el tope diario.</>}
                      {!alcanzaHoy && topeDiario === 0 && <> No hay ninguna línea conectada: no va a salir nada.</>}
                    </p>
                  </div>
                </div>
              </section>
            )}

            {/* ── Pendientes: misma consecuencia, causas opuestas, acciones opuestas ────────── */}
            {(sinCupo > 0 || fueraDeFranja > 0) && (
              <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <SecTitulo titulo="Por qué no salieron todos" sub="Cada motivo se destraba en un momento distinto" />
                <div className="space-y-2.5">
                  {sinCupo > 0 && (
                    <div className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50/50 p-3.5">
                      <Smartphone size={16} className="text-amber-600 flex-shrink-0" />
                      <p className="text-sm text-amber-900 flex-1"><strong>{num(sinCupo)}</strong> sin cupo — hay de nuevo <strong>mañana</strong>, o hoy si conectás otra línea; ahí tocá «Reanudar envío»</p>
                    </div>
                  )}
                  {fueraDeFranja > 0 && (
                    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
                      <Clock size={16} className="text-slate-500 flex-shrink-0" />
                      <p className="text-sm text-slate-700 flex-1">
                        <strong>{num(fueraDeFranja)}</strong> fuera de la franja horaria — cuando abra la ventana, tocá «Reanudar envío»
                        {franja && <span className="text-slate-500"> ({franja.desde}–{franja.hasta})</span>}
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* ── 6 · Salud de los WhatsApp ─────────────────────────────── */}
            <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
              <SecTitulo
                titulo="Salud de los WhatsApp"
                sub="Cupo diario: protege el número de un baneo y se renueva solo"
                right={<Link to="/activa/whatsapp" className="text-[11px] font-semibold text-violet-600 hover:text-violet-700">Administrar →</Link>}
              />
              {lines.length === 0 ? (
                <p className="text-sm text-slate-400 py-2">No hay líneas configuradas.</p>
              ) : (
                <div className="space-y-3">
                  {lines.slice(0, 8).map((l) => {
                    const usado = consumidoHoy(l);
                    const usoPct = pct(usado, l.msgsPorDia);
                    // el meter lleva la severidad: verde → ámbar → rojo a medida que se agota el cupo
                    const fill = usoPct > 85 ? "bg-red-600" : usoPct > 60 ? "bg-amber-500" : "bg-emerald-600";
                    const track = usoPct > 85 ? "bg-red-100" : usoPct > 60 ? "bg-amber-100" : "bg-emerald-100";
                    const renovado = usado === 0 && (l.enviadosHoy || 0) > 0; // el contador era de ayer
                    return (
                      <div key={l.id}>
                        <div className="flex items-center gap-3">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${l.conectada ? "bg-emerald-500" : "bg-slate-300"}`} />
                          <div className="w-32 sm:w-40 flex-shrink-0 min-w-0">
                            <p className="text-[13px] font-medium text-slate-700 truncate">{l.nombre}</p>
                            <p className="text-[10px] text-slate-400">{l.conectada ? l.numero || "conectada" : etiquetaEstadoLinea(l.estado)}</p>
                          </div>
                          <div className={`flex-1 h-2 rounded-full overflow-hidden ${l.conectada ? track : "bg-slate-100"}`}>
                            <div className={`h-full rounded-full transition-all duration-500 ${l.conectada ? fill : "bg-slate-300"}`} style={{ width: `${Math.max(1, Math.min(100, usoPct))}%` }} />
                          </div>
                          <span className="text-[11px] tabular-nums text-slate-500 w-20 text-right flex-shrink-0">{num(usado)}/{num(l.msgsPorDia)} hoy</span>
                        </div>
                        {/* el error de la línea, visible: sin esto "desconectada" no dice por qué */}
                        {!l.conectada && l.lastError && (
                          <p className="text-[11px] text-red-600 ml-5 mt-1 truncate" title={l.lastError}>⚠ {l.lastError}</p>
                        )}
                        {renovado && <p className="text-[11px] text-emerald-600 ml-5 mt-1">cupo renovado — el contador que ves era de ayer</p>}
                      </div>
                    );
                  })}
                  {lines.length > 8 && <p className="text-[11px] text-slate-400">y {lines.length - 8} líneas más</p>}
                  <p className="text-[11px] text-slate-400 pt-1.5 border-t border-slate-100">Son las líneas de la marca, no exclusivas de esta campaña.</p>
                </div>
              )}
            </section>

            </>)}

            {tab === "stats" && equipo && (<>
            {/* acciones (del equipo: reanudar y medir) */}
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => setConfirmarEnvio(true)} disabled={!!busy || sinLineas} className="h-11 px-5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2">
                {busy === "enviar" ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} Reanudar envío / reintentar rebotes
              </button>
              {!atribucionPendiente && (
                <button onClick={doMedir} disabled={!!busy} className="h-11 px-4 rounded-xl border border-violet-200 bg-white text-violet-700 text-sm font-semibold hover:bg-violet-50 disabled:opacity-50 inline-flex items-center gap-2">
                  {busy === "medir" ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={14} />} Volver a medir
                </button>
              )}
              {sinLineas && <span className="text-xs text-slate-400">Conectá una línea de WhatsApp para poder enviar.</span>}
            </div>
            </>)}
          </>
        )}

        {/* ── 7 · Ficha + bitácora, cada una en su pestaña ─────────────── */}
        <div className="space-y-3">
          {tab === "ficha" && (
          <div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
            <button onClick={() => setFicha((v) => !v)} className="w-full flex items-center justify-between gap-3 p-5 hover:bg-slate-50/50 transition-colors">
              <span className="text-sm font-semibold text-slate-700 flex items-center gap-2"><FileText size={14} className="text-slate-400" /> Ficha de la campaña</span>
              {ficha ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
            </button>
            {ficha && (
              <div className="px-5 pb-5 space-y-5 border-t border-slate-50 pt-5">
                {/* ── Procedencia ── de qué archivo salió y CUÁNDO salieron los mensajes.
                    No es cosmético: measure() atribuye las compras posteriores a la fecha de
                    envío y, si falta enviadoAt, cae en la del análisis — que es ANTERIOR — y
                    puede contar como "volvió por la campaña" algo comprado antes del mensaje. */}
                <div>
                  <p className="text-xs font-semibold text-slate-700 mb-2">Procedencia</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    {/* Mostraba «#c6e33b52c258», la huella del archivo: le sirve a soporte (dos cargas del mismo
                        archivo), no al dueño (QA del 08/10). A la vista, lo que el archivo traía; la huella, en el title. */}
                    <div className="rounded-lg bg-slate-50 px-3 py-2" title={campana.fileHash ? `Archivo #${campana.fileHash}` : undefined}>
                      <p className="text-slate-400 text-[10px] uppercase tracking-wider">Archivo</p>
                      <p className="text-[11px] text-slate-700 mt-0.5">
                        {campana.report?.totalClientes
                          ? <>{num(campana.report.totalClientes)} {plural(campana.report.totalClientes, "cliente", "clientes")}{campana.report.ordenesAnalizadas ? <> · {num(campana.report.ordenesAnalizadas)} {plural(campana.report.ordenesAnalizadas, "pedido", "pedidos")}</> : null}</>
                          : "—"}
                      </p>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      <p className="text-slate-400 text-[10px] uppercase tracking-wider">Mes del análisis</p>
                      <p className="text-[11px] text-slate-700 mt-0.5">{mesLargo(campana.periodo)}</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2">
                      <p className="text-slate-400 text-[10px] uppercase tracking-wider">Se analizó</p>
                      <p className="text-[11px] text-slate-700 mt-0.5">{fecha(campana.createdAt)}</p>
                    </div>
                    <div className={`rounded-lg px-3 py-2 ${campana.enviadoAt ? "bg-slate-50" : "bg-amber-50"}`}>
                      <p className="text-slate-400 text-[10px] uppercase tracking-wider">Salieron los mensajes</p>
                      <p className={`text-[11px] mt-0.5 ${campana.enviadoAt ? "text-slate-700" : "text-amber-800 font-semibold"}`}>
                        {campana.enviadoAt ? fecha(campana.enviadoAt) : "sin registrar"}
                      </p>
                    </div>
                  </div>
                  {!campana.enviadoAt && enviado && (
                    <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2.5">
                      <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-[11px] text-amber-800 leading-relaxed">
                        Esta campaña no registró fecha de envío, así que la medición usa la del <strong>análisis</strong>,
                        que es anterior: puede contar compras hechas <strong>antes</strong> de que la
                        persona recibiera el mensaje. El número de "volvieron" está inflado hacia arriba.
                      </p>
                    </div>
                  )}
                </div>

                {/* ── Consentimiento aplicado ── las dos preguntas que el cliente hace SIEMPRE:
                    "¿por qué le mandaste a 800 si mi base tiene 1200?" y "¿con qué derecho?".
                    El backend persiste esto como evidencia; vivía sólo en el response del envío. */}
                {(campana.stats?.excluidosSinOptIn !== undefined || campana.stats?.bloqueadosDnc !== undefined || campana.stats?.politicaOptIn) && (
                  <div>
                    <p className="text-xs font-semibold text-slate-700 mb-2">Consentimiento aplicado (Ley 25.326)</p>
                    <div className="flex items-center gap-2 flex-wrap text-xs mb-2">
                      {/* Decía «Política estricta / declarada»: el valor interno de la configuración, que el dueño
                          no eligió con ese nombre (QA del 08/10). Ahora dice a quién se le escribió. */}
                      {campana.stats?.politicaOptIn === "estricta" && (
                        <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 inline-flex items-center gap-1">
                          <ShieldCheck size={12} /> Se les escribió <strong>sólo</strong> a quienes dijeron que sí a recibir mensajes
                        </span>
                      )}
                      {campana.stats?.politicaOptIn === "declarada" && (
                        <span className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-800 inline-flex items-center gap-1">
                          <AlertTriangle size={12} /> Se les escribió a todos <strong>menos</strong> a quienes dijeron que no: la marca afirmó tener su permiso
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      <div className="rounded-lg bg-slate-50 px-3 py-2">
                        <p className="text-slate-400 text-[10px] uppercase tracking-wider">No se les escribió · sin consentimiento</p>
                        <p className="text-slate-800 font-bold tabular-nums mt-0.5">{num(campana.stats?.excluidosSinOptIn || 0)}</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 px-3 py-2">
                        <p className="text-slate-400 text-[10px] uppercase tracking-wider">Bloqueados · pidieron no ser contactados</p>
                        <p className="text-slate-800 font-bold tabular-nums mt-0.5">{num(campana.stats?.bloqueadosDnc || 0)}</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2">{hayDatosSimulados ? "Son los números de este envío, que fue de prueba: dicen a quién se le habría escrito." : "Son los números del envío real."} Es la respuesta a «¿por qué le mandaste a menos gente que la que tiene mi base?».</p>
                  </div>
                )}

                {campana.context && (
                  <div>
                    <p className="text-xs font-semibold text-slate-700 mb-2">Contexto con el que se generaron los mensajes</p>
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600">Tono: <strong>{campana.context.tono}</strong></span>
                      <span className="px-2.5 py-1 rounded-lg bg-violet-50 text-violet-700 inline-flex items-center gap-1"><ShieldCheck size={12} /> Techo: <strong>{campana.context.techoPct}%</strong></span>
                      <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600">Canales: {campana.context.canales}</span>
                      {campana.context.usaDescuentos === false && (
                        <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600">Sin descuentos</span>
                      )}
                      {/* Los puntos son plata de la marca: la ficha de una campaña ya enviada tiene
                          que decir cuántos se comprometieron y hasta cuándo valen — es la respuesta
                          a "¿por qué me está viniendo gente a canjear puntos en noviembre?". */}
                      {campana.context.programaPuntos && Number(campana.context.puntos?.porPersona) > 0 && (
                        <span className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 inline-flex items-center gap-1">
                          <Star size={12} /> {Number(campana.context.puntos?.porPersona).toLocaleString("es-AR")} {(campana.context.puntos?.nombre || "puntos")}
                          {Number(campana.context.puntos?.vigenciaDias) > 0
                            ? <> · vencen a los {campana.context.puntos?.vigenciaDias} días</>
                            : <> · sin vencimiento</>}
                        </span>
                      )}
                    </div>
                    {enviado && <p className="text-[11px] text-slate-400 mt-1.5">Ya no se puede cambiar: los mensajes salieron con esta configuración.</p>}
                  </div>
                )}
                {campana.grupos?.some((g) => g.mensaje) && (
                  <div>
                    <p className="text-xs font-semibold text-slate-700 mb-2">Mensajes que se enviaron</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {campana.grupos.filter((g) => g.mensaje).map((g) => {
                        // la causa real: decía siempre «el techo lo bajó», también cuando lo bajó el % que eligió la marca
                        const baja = porQueBajoElDescuento(g, campana.context);
                        return (
                          <div key={g.id} className="rounded-xl border border-slate-100 bg-slate-50/40 p-3">
                            <p className="text-xs font-semibold text-slate-800">{g.emoji} {g.nombre}</p>
                            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{g.mensaje}</p>
                            <p className="text-[10px] text-slate-400 mt-1.5">
                              {g.canal} · {etiquetaOferta(g)}
                              {/* el % lo eligió el equipo: al cliente no se le dice «que elegiste» */}
                              {baja && <span className="text-emerald-600 font-semibold"> · {textoBajaDescuento(baja, { paraCliente: !equipo })}</span>}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {rep && (
                  <div>
                    <p className="text-xs font-semibold text-slate-700 mb-2">Calidad de la base</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { l: "Clientes", v: num(rep.totalClientes) },
                        { l: "Sin contacto", v: num(rep.sinContacto) },
                        { l: "Cel. duplicados", v: num(rep.duplicadosCelular) },
                        { l: "Historia", v: `${rep.mesesHistoria} meses` },
                        /* Sólo si hay algo que contar, y con `?? 0`: las campañas viejas no traen
                           estos campos. Un mail que no era mail ("-", "N/A") dejó a esa persona sin
                           mail; un ID repetido fusionó varias filas del archivo en una sola ficha. */
                        ...((rep.mailsInvalidos ?? 0) > 0 ? [{ l: "Mails inválidos", v: num(rep.mailsInvalidos ?? 0) }] : []),
                        ...((rep.idsDuplicados ?? 0) > 0 ? [{ l: "IDs repetidos", v: num(rep.idsDuplicados ?? 0) }] : []),
                      ].map((x) => (
                        <div key={x.l} className="rounded-xl bg-slate-50 p-3">
                          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{x.l}</p>
                          <p className="text-base font-semibold text-slate-800 mt-0.5">{x.v}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">Los "sin contacto" explican por qué el universo contactable es menor que la base total.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          )}

          {/* Bitácora: el detalle mensaje por mensaje vive con los Mensajes */}
          {tab === "mensajes" && enviado && (
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
              <button onClick={() => setLogAbierto((v) => !v)} className="w-full flex items-center justify-between gap-3 p-5 hover:bg-slate-50/50 transition-colors">
                <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <ScrollText size={14} className="text-slate-400" /> Bitácora · mensaje por mensaje
                  {bitacora && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{num(bitacora.total)}</span>}
                </span>
                {logAbierto ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
              </button>

              {logAbierto && (
                <div className="px-5 pb-5 border-t border-slate-50 pt-4 space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {[
                      { k: "", l: "Todos" }, { k: "leido", l: "Leídos" }, { k: "entregado", l: "Entregados" },
                      { k: "enviado", l: "Enviados" }, { k: "fallido", l: "Fallidos" },
                    ].map((f) => (
                      <button key={f.k} onClick={() => filtrarLog(f.k)}
                        className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-colors ${logFiltro === f.k ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                        {f.l}
                      </button>
                    ))}
                    <div className="flex-1" />
                    {bitacora && bitacora.items.length > 0 && (
                      <button onClick={exportBitacora} className="h-7 px-2.5 rounded-lg border border-slate-200 bg-white text-[11px] font-semibold text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5">
                        <Download size={12} /> CSV
                      </button>
                    )}
                  </div>

                  {logLoading && !bitacora ? (
                    <div className="py-10 text-center"><Loader2 size={20} className="animate-spin text-violet-500 mx-auto" /></div>
                  ) : !bitacora || bitacora.items.length === 0 ? (
                    <p className="text-sm text-slate-400 py-6 text-center">No hay mensajes con este filtro.</p>
                  ) : (
                    <>
                      {/* sin skeleton al refiltrar: se mantiene el render y baja la opacidad */}
                      <div className={`overflow-x-auto -mx-1 transition-opacity ${logLoading ? "opacity-40" : ""}`}>
                        <table className="w-full min-w-[760px] text-sm">
                          <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left border-b border-slate-100">
                              <th className="px-3 py-2 font-semibold">Persona</th>
                              <th className="px-3 py-2 font-semibold">Grupo</th>
                              <th className="px-3 py-2 font-semibold">Canal</th>
                              <th className="px-3 py-2 font-semibold">Destino</th>
                              <th className="px-3 py-2 font-semibold">Estado</th>
                              <th className="px-3 py-2 font-semibold text-right">Int.</th>
                              <th className="px-3 py-2 font-semibold">Detalle</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {bitacora.items.map((e) => (
                              <tr key={e.id} className="hover:bg-slate-50/50">
                                <td className="px-3 py-2.5 font-medium text-slate-700 truncate max-w-[130px]">{e.personaId}</td>
                                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{e.grupo || "—"}</td>
                                <td className="px-3 py-2.5 text-slate-500">{String(e.canal).includes("WhatsApp") ? "WhatsApp" : "Email"}</td>
                                <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">{e.numeroDestino || "—"}</td>
                                <td className="px-3 py-2.5"><EstadoEntrega e={e} /></td>
                                <td className="px-3 py-2.5 text-right tabular-nums text-slate-400">{e.intentos ?? "—"}</td>
                                <td className="px-3 py-2.5 text-[11px] text-slate-500 max-w-[200px] truncate">
                                  {e.motivoError ? <span className="text-red-600">{e.motivoError}</span>
                                    : e.baja ? <span className="text-red-600 font-semibold">pidió baja</span>
                                    : e.respondio ? <span className="text-violet-600">"{e.respuesta}"</span>
                                    : e.volvioAComprar ? <span className="text-emerald-600 font-semibold">volvió a comprar</span>
                                    : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="flex items-center justify-between gap-3 pt-1">
                        <p className="text-[11px] text-slate-400">
                          Página {bitacora.page} de {bitacora.paginas} · {num(bitacora.total)} mensajes
                          {bitacora.enmascarado && <span className="ml-1.5 text-slate-300">· contactos enmascarados</span>}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <button disabled={bitacora.page <= 1 || logLoading} onClick={() => cargarBitacora(bitacora.page - 1)}
                            className="h-7 w-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center justify-center"><ChevronLeft size={14} /></button>
                          <button disabled={bitacora.page >= bitacora.paginas || logLoading} onClick={() => cargarBitacora(bitacora.page + 1)}
                            className="h-7 w-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center justify-center"><ChevronRight size={14} /></button>
                        </div>
                      </div>

                      <p className="text-[11px] text-slate-400 leading-relaxed border-t border-slate-100 pt-2.5">
                        Es el <strong>estado actual</strong> de cada mensaje, una fila por persona — no el historial de cada
                        intento (de los reintentos sólo queda el contador). Los contactos se muestran enmascarados.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Modal de confirmación — esta acción manda mensajes reales a clientes reales. Sin grupos
          ni líneas: el backend reanuda con el alcance del primer envío (y lo dice en el dry-run). */}
      {confirmarEnvio && (
        <ConfirmarEnvio
          campaignId={id}
          titulo="¿Reanudar el envío?"
          mostrarReintentos
          onConfirmar={doEnviar}
          onCerrar={() => setConfirmarEnvio(false)}
        />
      )}
    </div>
  );
}
